import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, CardBody, CardHeader, Container, Row, Col, Input, Label, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { APIClient, setAuthorization } from "../../helpers/api_helper";
import ToastAlert from "../../Components/Common/ToastAlert";

const SocialPosts = () => {
    const api = useMemo(() => new APIClient(), []);
    setAuthorization(localStorage.getItem("token"));

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("pending_approval");
    const [feedbackOpen, setFeedbackOpen] = useState({});
    const [feedbackText, setFeedbackText] = useState({});
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);

    const statusColor = { pending_revision: "secondary", pending_approval: "warning", approved: "success", rejected: "danger" };
    const platformColor = { linkedin: "primary", twitter: "info", facebook: "secondary" };

    const fetchPosts = async (p = page) => {
        setLoading(true);
        try {
            const response = await api.get(`v1/content/social-posts?status=${status}&page=${p}&page_size=10`);
            setPosts(response?.data || []);
            setMaxPage(response?.max_page || 1);
        } catch (e) {
            if (e.message?.includes("503")) {
                ToastAlert("Content engine database unavailable", "warning");
            } else {
                ToastAlert(e.message, "error");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setPage(1); fetchPosts(1); }, [status]);
    useEffect(() => { fetchPosts(); }, [page]);

    const approve = async (id) => {
        try {
            await api.patch(`v1/content/social-posts/${id}/approve`);
            fetchPosts();
        } catch (e) { ToastAlert(e.message, "error"); }
    };

    const reject = async (id) => {
        try {
            await api.patch(`v1/content/social-posts/${id}/reject`);
            fetchPosts();
        } catch (e) { ToastAlert(e.message, "error"); }
    };

    const submitFeedback = async (id) => {
        const text = feedbackText[id] || "";
        if (!text.trim()) return;
        try {
            await api.patch(`v1/content/social-posts/${id}/feedback`, { feedback: text });
            setFeedbackOpen(prev => ({ ...prev, [id]: false }));
            setFeedbackText(prev => ({ ...prev, [id]: "" }));
            fetchPosts();
        } catch (e) { ToastAlert(e.message, "error"); }
    };

    const toggleFeedback = (id) => setFeedbackOpen(prev => ({ ...prev, [id]: !prev[id] }));

    document.title = "Social posts · Horus";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Social posts" pageTitle="Content" />
                    <Row className="mb-3">
                        <Col xs="auto">
                            <Label className="me-2">Status:</Label>
                            {["pending_approval", "approved", "rejected"].map(s => (
                                <Button key={s} size="sm" color={status === s ? "primary" : "light"} className="me-1"
                                    onClick={() => setStatus(s)}>
                                    {s.replace(/_/g, " ")}
                                </Button>
                            ))}
                        </Col>
                    </Row>
                    {loading ? (
                        <div className="text-center py-5"><Spinner color="primary" /></div>
                    ) : posts.length === 0 ? (
                        <p className="text-muted">No social posts found.</p>
                    ) : (
                        <>
                            {posts.map(post => (
                                <Card key={post.id} className="mb-3">
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Badge color={platformColor[post.platform] || "secondary"} className="me-2">{post.platform}</Badge>
                                            <Badge color={statusColor[post.status] || "secondary"}>{post.status}</Badge>
                                            {post.content_queue_id && <small className="text-muted ms-2">Article #{post.content_queue_id}</small>}
                                        </div>
                                        {post.status === "pending_approval" && (
                                            <div className="d-flex gap-2">
                                                <Button size="sm" color="success" onClick={() => approve(post.id)}>Approve</Button>
                                                <Button size="sm" color="danger" onClick={() => reject(post.id)}>Reject</Button>
                                                <Button size="sm" color="warning" onClick={() => toggleFeedback(post.id)}>Feedback</Button>
                                            </div>
                                        )}
                                    </CardHeader>
                                    {feedbackOpen[post.id] && (
                                        <CardBody className="border-bottom">
                                            <Input
                                                type="textarea"
                                                rows={3}
                                                placeholder="Enter feedback..."
                                                value={feedbackText[post.id] || ""}
                                                onChange={e => setFeedbackText(prev => ({ ...prev, [post.id]: e.target.value }))}
                                            />
                                            <div className="d-flex gap-2 mt-2">
                                                <Button size="sm" color="primary" onClick={() => submitFeedback(post.id)}>Submit</Button>
                                                <Button size="sm" color="light" onClick={() => toggleFeedback(post.id)}>Cancel</Button>
                                            </div>
                                        </CardBody>
                                    )}
                                    <CardBody>
                                        <p style={{ whiteSpace: "pre-wrap" }}>{post.post_text}</p>
                                        {post.feedback && <div className="mt-2 p-2 bg-light"><strong>Feedback:</strong> {post.feedback}</div>}
                                    </CardBody>
                                </Card>
                            ))}
                            {maxPage > 1 && (
                                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                                    <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                                    <span className="text-muted">Page {page} of {maxPage}</span>
                                    <Button size="sm" color="light" disabled={page >= maxPage} onClick={() => setPage(p => p + 1)}>Next</Button>
                                </div>
                            )}
                        </>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default SocialPosts;
